import { useState, useRef, useEffect } from 'react';
import {
  eventCategoryLabels, eventCategoryIcons,
  eventStatusLabels,
  eventModalityLabels, eventModalityIcons,
  type EventCategory, type EventStatus, type EventModality,
} from '@/mocks/adminEvents';

export type TimeFilter = 'hoy' | 'esta-semana' | 'este-mes' | 'proximos' | '';

interface FilterBarProps {
  search: string;
  onSearchChange: (val: string) => void;
  category: string;
  onCategoryChange: (val: string) => void;
  status: string;
  onStatusChange: (val: string) => void;
  modality: string;
  onModalityChange: (val: string) => void;
  timeFilter: TimeFilter;
  onTimeFilterChange: (val: TimeFilter) => void;
  categories: EventCategory[];
}

export default function FilterBar({
  search, onSearchChange,
  category, onCategoryChange,
  status, onStatusChange,
  modality, onModalityChange,
  timeFilter, onTimeFilterChange,
  categories,
}: FilterBarProps) {
  const [catOpen, setCatOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [modOpen, setModOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const statusRef = useRef<HTMLDivElement>(null);
  const modRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) setCatOpen(false);
      if (statusRef.current && !statusRef.current.contains(e.target as Node)) setStatusOpen(false);
      if (modRef.current && !modRef.current.contains(e.target as Node)) setModOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const hasFilters = category || status || modality;

  const clearAll = () => {
    onCategoryChange('');
    onStatusChange('');
    onModalityChange('');
    onSearchChange('');
    onTimeFilterChange('');
  };

  const activeLabel = () => {
    const parts: string[] = [];
    if (status === 'publicado') parts.push('Publicados');
    else if (status === 'finalizado') parts.push('Finalizados');
    else if (status === 'cancelado') parts.push('Cancelados');
    if (category) parts.push(eventCategoryLabels[category as EventCategory] || category);
    if (modality) parts.push(eventModalityLabels[modality as EventModality] || modality);
    return parts.length > 0 ? parts.join(' · ') : 'Todos los eventos';
  };

  const timeFilters: { value: TimeFilter; label: string; icon: string }[] = [
    { value: 'hoy', label: 'Hoy', icon: 'ri-calendar-check-line' },
    { value: 'esta-semana', label: 'Esta semana', icon: 'ri-calendar-2-line' },
    { value: 'este-mes', label: 'Este mes', icon: 'ri-calendar-line' },
    { value: 'proximos', label: 'Próximos', icon: 'ri-calendar-event-line' },
  ];

  const modalities: EventModality[] = ['presencial', 'online', 'hibrido'];
  const categoryIconsMap: Record<EventCategory, string> = {
    musica: 'ri-music-2-line', deportes: 'ri-football-line', comunidad: 'ri-group-line',
    cultural: 'ri-palette-line', religioso: 'ri-cross-line', familiar: 'ri-hearts-line', educativo: 'ri-book-open-line',
  };

  return (
    <div className="space-y-3">
      {/* Quick time filters */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {timeFilters.map((tf) => (
          <button
            key={tf.value}
            onClick={() => onTimeFilterChange(timeFilter === tf.value ? '' : tf.value)}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[11px] font-bold cursor-pointer transition-all whitespace-nowrap border ${
              timeFilter === tf.value
                ? 'bg-primary-500 text-background-50 border-primary-500'
                : 'bg-background-50 text-foreground-600 border-background-200 hover:border-foreground-300'
            }`}
          >
            <i className={`${tf.icon} text-xs`}></i>
            {tf.label}
          </button>
        ))}
        {timeFilter && (
          <button
            onClick={() => onTimeFilterChange('')}
            className="inline-flex items-center gap-1 px-2.5 py-2 text-[10px] font-semibold text-foreground-400 hover:text-foreground-600 cursor-pointer transition-colors whitespace-nowrap"
          >
            <i className="ri-close-line"></i> Quitar filtro
          </button>
        )}
      </div>

      {/* Search + clear */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="relative flex-1 w-full">
          <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Buscar eventos por título, descripción o ubicación..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all"
          />
        </div>
        {hasFilters && (
          <button
            onClick={clearAll}
            className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 cursor-pointer transition-colors whitespace-nowrap shrink-0"
          >
            <i className="ri-close-circle-line"></i> Limpiar filtros
          </button>
        )}
      </div>

      {/* Filter pills */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-bold text-foreground-400 uppercase tracking-wider whitespace-nowrap">
          {activeLabel()}
        </span>

        {/* Status dropdown */}
        <div ref={statusRef} className="relative">
          <button
            onClick={() => setStatusOpen(!statusOpen)}
            className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-[11px] font-bold cursor-pointer transition-all whitespace-nowrap border ${
              status ? 'bg-primary-500 text-background-50 border-primary-500' : 'bg-background-50 text-foreground-600 border-background-200 hover:border-foreground-300'
            }`}
          >
            <i className="ri-flag-line text-xs"></i>
            {status ? eventStatusLabels[status as EventStatus] : 'Estado'}
            <i className={`text-[9px] ${statusOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
          </button>
          {statusOpen && (
            <div className="absolute top-full mt-1.5 left-0 bg-background-50 border border-background-200 rounded-xl shadow-sm z-30 py-1.5 min-w-[160px]">
              <button onClick={() => { onStatusChange(''); setStatusOpen(false); }} className="w-full text-left px-4 py-2 text-[11px] font-medium text-foreground-500 hover:bg-background-100 whitespace-nowrap">Todos</button>
              <button onClick={() => { onStatusChange('publicado'); setStatusOpen(false); }} className="w-full text-left px-4 py-2 text-[11px] font-medium text-emerald-600 hover:bg-emerald-50 whitespace-nowrap">Publicado</button>
              <button onClick={() => { onStatusChange('finalizado'); setStatusOpen(false); }} className="w-full text-left px-4 py-2 text-[11px] font-medium text-foreground-500 hover:bg-background-100 whitespace-nowrap">Finalizado</button>
              <button onClick={() => { onStatusChange('cancelado'); setStatusOpen(false); }} className="w-full text-left px-4 py-2 text-[11px] font-medium text-red-600 hover:bg-red-50 whitespace-nowrap">Cancelado</button>
            </div>
          )}
        </div>

        {/* Modality dropdown */}
        <div ref={modRef} className="relative">
          <button
            onClick={() => setModOpen(!modOpen)}
            className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-[11px] font-bold cursor-pointer transition-all whitespace-nowrap border ${
              modality ? 'bg-secondary-500 text-background-50 border-secondary-500' : 'bg-background-50 text-foreground-600 border-background-200 hover:border-foreground-300'
            }`}
          >
            <i className="ri-map-pin-line text-xs"></i>
            {modality ? eventModalityLabels[modality as EventModality] : 'Modalidad'}
            <i className={`text-[9px] ${modOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
          </button>
          {modOpen && (
            <div className="absolute top-full mt-1.5 left-0 bg-background-50 border border-background-200 rounded-xl shadow-sm z-30 py-1.5 min-w-[160px]">
              <button onClick={() => { onModalityChange(''); setModOpen(false); }} className="w-full text-left px-4 py-2 text-[11px] font-medium text-foreground-500 hover:bg-background-100 whitespace-nowrap">Todas</button>
              {modalities.map((m) => (
                <button key={m} onClick={() => { onModalityChange(m); setModOpen(false); }} className={`w-full text-left px-4 py-2 text-[11px] font-medium hover:bg-background-100 whitespace-nowrap`}>
                  <i className={`${eventModalityIcons[m]} text-[10px] mr-2`}></i>
                  {eventModalityLabels[m]}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Category dropdown */}
        <div ref={catRef} className="relative">
          <button
            onClick={() => setCatOpen(!catOpen)}
            className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-[11px] font-bold cursor-pointer transition-all whitespace-nowrap border ${
              category ? 'bg-accent-500 text-background-50 border-accent-500' : 'bg-background-50 text-foreground-600 border-background-200 hover:border-foreground-300'
            }`}
          >
            <i className="ri-price-tag-3-line text-xs"></i>
            {category ? eventCategoryLabels[category as EventCategory] : 'Categoría'}
            <i className={`text-[9px] ${catOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
          </button>
          {catOpen && (
            <div className="absolute top-full mt-1.5 left-0 bg-background-50 border border-background-200 rounded-xl shadow-sm z-30 py-1.5 min-w-[160px] max-h-[240px] overflow-y-auto">
              <button onClick={() => { onCategoryChange(''); setCatOpen(false); }} className="w-full text-left px-4 py-2 text-[11px] font-medium text-foreground-500 hover:bg-background-100 whitespace-nowrap">Todas</button>
              {categories.map((c) => (
                <button key={c} onClick={() => { onCategoryChange(c); setCatOpen(false); }} className="w-full text-left px-4 py-2 text-[11px] font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap">
                  <i className={`${categoryIconsMap[c] || eventCategoryIcons[c]} text-[10px] mr-2`}></i>
                  {eventCategoryLabels[c]}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}